/**
 * Fixed top navigation bar rendered app-wide from App.tsx. Shows the porsui
 * logo (links home), bounties/search links (plus admin for admins, bolded when
 * active), and an account section driven by UserContext: logged-out users get
 * a "log in" button, logged-in users get their avatar/username with a hover
 * dropdown (create bounty, listings, pursuits, submissions, log out via
 * UserAPI.logout + clearUserContext).
 */
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useUserState, clearUserContext, useUserDispatch } from "@/contexts/UserContext";
import { UserAPI } from "@/api/user";

const Logo: React.FC = () => (
    <div className="flex-shrink-0">
        <Link to="/" className="font-bold text-gray-800 hover:text-gray-600">
            <img src="/porsui-logo.png" className="mt-[-4px] w-20 object-contain max-w-[60px]" />
        </Link>
    </div>
);

const Navigation: React.FC = () => {
    const user = useUserState();
    const location = useLocation();
    return (
        <div className="flex items-center space-x-6">
            {user?.isAdmin && (
                <Link
                    to="/admin"
                    className={
                        location.pathname.startsWith("/admin")
                            ? "font-porsui font-bold"
                            : "font-porsui font-normal"
                    }
                >
                    admin
                </Link>
            )}
            <Link
                to="/bounties"
                className={
                    location.pathname.startsWith("/bounties")
                        ? "font-porsui font-bold"
                        : "font-porsui font-normal"
                }
            >
                bounties
            </Link>
            <Link
                to="/search"
                className={
                    location.pathname.startsWith("/search")
                        ? "font-porsui font-bold"
                        : "font-porsui font-normal"
                }
            >
                search
            </Link>
        </div>
    );
};

const AccountSection: React.FC<{
    dropdownVisible: boolean;
    setDropdownVisible: (visible: boolean) => void;
}> = ({ dropdownVisible, setDropdownVisible }) => {
    const user = useUserState();
    const userDispatch = useUserDispatch();
    const navigate = useNavigate();

    const dropdownItems: {
        label: string;
        onClick: () => void;
    }[] = [
        {
            label: "create bounty",
            onClick: () => navigate("/account/create"),
        },
        {
            label: "listings",
            onClick: () => navigate("/account/listings"),
        },
        {
            label: "pursuits",
            onClick: () => navigate("/account/pursuits"),
        },
        {
            label: "submissions",
            onClick: () => navigate("/account/submissions"),
        },
        {
            label: "log out",
            onClick: async () => {
                try {
                    await UserAPI.logout();
                } catch (error) {
                    // Even if logout fails, clear local state
                    console.error("Logout error:", error);
                }
                clearUserContext(userDispatch);
                navigate("/signUpLogin");
            },
        },
    ];

    return (
        <div className="relative flex items-center justify-end flex-shrink-0">
            <button
                onClick={() => {
                    if (user) {
                        navigate("/account/profile");
                    } else {
                        navigate("/signUpLogin");
                    }
                }}
                onMouseEnter={() => setDropdownVisible(true)}
                className={`
                    flex items-center space-x-2 bg-white text-slate-950 font-porsui
                    rounded-md transition-all duration-200 hover:scale-105"
                `}
            >
                {user ? (
                    <div className="flex items-center">
                        {user.profilePictureUrl ? (
                            <img
                                src={user.profilePictureUrl}
                                alt="Profile"
                                className="h-6 w-6 mr-2 rounded-full overflow-hidden object-cover flex-shrink-0"
                            />
                        ) : (
                            <div className="h-6 w-6 mr-2 rounded-full overflow-hidden bg-slate-950" />
                        )}
                        <span>{user.username}</span>
                    </div>
                ) : (
                    "log in"
                )}
            </button>

            {dropdownVisible && user && (
                <div
                    className={`absolute right-0 mt-4 w-48 bg-white shadow-sm text-slate-950
                        transition-all duration-200 ease-in-out origin-top font-porsui 
                        ${
                            dropdownVisible
                                ? "opacity-100 scale-y-100 translate-y-0"
                                : "opacity-0 scale-y-95 -translate-y-2 pointer-events-none"
                        }`}
                    onMouseLeave={() => setDropdownVisible(false)}
                    style={{ top: "100%", zIndex: 50 }}
                >
                    {dropdownItems.map((item) => (
                        <button
                            key={item.label}
                            onClick={item.onClick}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const TopNavBar: React.FC = () => {
    const [accountDropdownVisible, setAccountDropdownVisible] = useState<boolean>(false);

    // Handle click outside for account dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                accountDropdownVisible &&
                !(event.target as HTMLElement).closest(".dropdown-container")
            ) {
                setAccountDropdownVisible(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [accountDropdownVisible]);

    return (
        <nav className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50 w-full">
            <div className="w-full mx-auto" onMouseLeave={() => setAccountDropdownVisible(false)}>
                <div className="flex justify-between items-center h-16 mx-4">
                    <div className="flex-1 flex items-center min-w-0">
                        <Logo />
                        <div className="mx-4 md:mx-8 overflow-x-auto whitespace-nowrap">
                            <Navigation />
                        </div>
                    </div>
                    <AccountSection
                        dropdownVisible={accountDropdownVisible}
                        setDropdownVisible={setAccountDropdownVisible}
                    />
                </div>
            </div>
        </nav>
    );
};

export default TopNavBar;
